// src/pages/FirmPortal.js
// MODIFIED: Added AlertBadge in header and useClientAlerts hook
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Chip, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import SupportModal from '../components/SupportModal';
import FirmPortalFeed from '../components/Admin/FirmPortalFeed';
import FirmPortalAccount from '../components/Admin/FirmPortalAccount';
import FirmPortalDashboard from '../components/Admin/FirmPortalDashboard';
import FirmPortalClients from '../components/Admin/FirmPortalClients';
import FirmPortalUsers from '../components/Admin/FirmPortalUsers';
import FirmPortalAnalytics from '../components/Admin/FirmPortalAnalytics';
import FirmPortalAIGovernance from '../components/Admin/FirmPortalAIGovernance';
import AlertBadge from '../components/Alerts/AlertBadge'; // NEW
import useOrganizationConfig from '../hooks/useOrganizationConfig';
import { useClientAlerts } from '../hooks/useClientAlerts'; // NEW
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import ClientAlertsSection from '../components/Alerts/ClientAlertsSection';

const FirmPortal = () => {
    const { user, logout, userPortalAccess } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { config: orgConfig, loading: configLoading } = useOrganizationConfig(user?.organizationId);
    
    // Organization data for tier checking
    const [orgData, setOrgData] = useState(null);
    const [orgLoading, setOrgLoading] = useState(true);
    
    // Tab state
    const [activeTab, setActiveTab] = useState(0);
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
    const [initialSearchQuery, setInitialSearchQuery] = useState('');

    // Determine access levels
    // Only show firm portal toggle if user has org access AND the org actually exists
const [firmOrgExists, setFirmOrgExists] = useState(false);

useEffect(() => {
    const checkOrgExists = async () => {
        if (user?.organizationId && user?.isOrgAdmin) {
            try {
                const orgRef = doc(db, 'organizations', user.organizationId);
                const orgSnap = await getDoc(orgRef);
                setFirmOrgExists(orgSnap.exists());
            } catch (err) {
                setFirmOrgExists(false);
            }
        } else {
            setFirmOrgExists(false);
        }
    };
    checkOrgExists();
}, [user?.organizationId, user?.isOrgAdmin]);

const isDualAccessUser = user?.isOrgAdmin && userPortalAccess && firmOrgExists;
    
    // Enterprise access check
    const isEnterprise = orgData?.firmPortalTier === 'enterprise';
    const isAdmin = user?.firmPortalRole === 'admin';  // Only check firmPortalRole, not isOrgAdmin
    const showEnterpriseFeatures = isEnterprise;  // Both admin and user roles see enterprise tabs
    const showUserManagement = isEnterprise && isAdmin;  // Only admins can manage users

    // NEW: Client alerts hook
    const { 
        alerts, 
        clientsWithAlertsCount,
        loading: alertsLoading,
        dismissAlert,
        dismissAllClientAlerts,
        refetch: refetchAlerts
    } = useClientAlerts({
        organizationId: user?.organizationId,
        userId: user?.uid,
        firmPortal: true
    });

    // Fetch organization data for tier checking
useEffect(() => {
    const fetchOrgData = async () => {
        if (!user?.organizationId) {
            setOrgData(null);  // Explicitly set to null
            setOrgLoading(false);
            return;
        }
        
        try {
            const orgRef = doc(db, 'organizations', user.organizationId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
                setOrgData({ id: orgSnap.id, ...orgSnap.data() });
            } else {
                // Organization doesn't exist (was deleted)
                console.warn(`Organization ${user.organizationId} not found - may have been deleted`);
                setOrgData(null);
            }
        } catch (err) {
            console.error('Error fetching organization data:', err);
            setOrgData(null);
        } finally {
            setOrgLoading(false);
        }
    };
    
    fetchOrgData();
}, [user?.organizationId]);

    useEffect(() => {
        if (user) {
            console.log('[FIRM PORTAL DEBUG] User object:', user);
            console.log('[FIRM PORTAL DEBUG] organizationId:', user.organizationId);
            console.log('[FIRM PORTAL DEBUG] isDualAccessUser:', isDualAccessUser);
            console.log('[FIRM PORTAL DEBUG] isEnterprise:', isEnterprise);
            console.log('[FIRM PORTAL DEBUG] isAdmin:', isAdmin);
            console.log('[FIRM PORTAL DEBUG] showEnterpriseFeatures:', showEnterpriseFeatures);
            
            // Save portal preference for dual-access users
            if (isDualAccessUser) {
                localStorage.setItem('lastPortalView', 'firm-portal');
            }
        }
    }, [user, isDualAccessUser, isEnterprise, isAdmin, showEnterpriseFeatures]);

    const handleSwitchToUserPortal = () => {
        navigate('/client-dashboard/insights');
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handle alert badge click - navigate to appropriate tab
    const handleAlertBadgeClick = () => {
        if (showEnterpriseFeatures) {
            // Enterprise: navigate to Dashboard tab
            const dashboardIndex = tabs.findIndex(t => t.value === 'dashboard');
            if (dashboardIndex !== -1) {
                setActiveTab(dashboardIndex);
            }
        } else {
            // Read-only: navigate to Activity tab (alerts shown there)
            const activityIndex = tabs.findIndex(t => t.value === 'activity');
            if (activityIndex !== -1) {
                setActiveTab(activityIndex);
            }
        }
    };

    // Define tabs based on enterprise access
     const baseTabs = [
        { label: 'Activity', value: 'activity' },
        { label: 'Account', value: 'account' }
    ];

    const enterpriseTabs = [
        { label: 'Dashboard', value: 'dashboard' },
        { label: 'Activity', value: 'activity' },
        { label: 'Clients', value: 'clients' },
        ...(showUserManagement ? [{ label: 'Users', value: 'users' }] : []),
        { label: 'Analytics', value: 'analytics' },
        { label: 'AI Governance', value: 'ai-governance' },
        { label: 'Account', value: 'account' }
    ];

    // Derive tabs reactively
    const tabs = showEnterpriseFeatures ? enterpriseTabs : baseTabs;

    // Handle URL parameters for deep linking (e.g., ?tab=activity&search=abc123)
    const urlParamsProcessed = useRef(false);

    useEffect(() => {
        if (urlParamsProcessed.current) return;
        if (orgLoading || configLoading) return;
        if (!tabs || tabs.length === 0) return;

        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const searchParam = params.get('search');

        if (tabParam) {
            const tabIndex = tabs.findIndex(t => t.value === tabParam);
            if (tabIndex !== -1) {
                setActiveTab(tabIndex);
            }
        }

        if (searchParam) {
            setInitialSearchQuery(searchParam);
        }

        urlParamsProcessed.current = true;
    }, [location.search, tabs, orgLoading, configLoading]);

    // Render tab content based on current tab
    const renderTabContent = () => {
        const currentTab = tabs[activeTab]?.value;
        
        switch (currentTab) {
            case 'dashboard':
                return (
                    <FirmPortalDashboard 
                        user={user} 
                        orgData={orgData}
                        orgConfig={orgConfig}
                        alerts={alerts}
                        alertsLoading={alertsLoading}
                        onDismissAlert={dismissAlert}
                        onDismissAllAlerts={dismissAllClientAlerts}
                        onRefetchAlerts={refetchAlerts}
                        onNavigateToClient={(clientId) => {
                            const clientsIndex = tabs.findIndex(t => t.value === 'clients');
                            if (clientsIndex !== -1) {
                                setActiveTab(clientsIndex);
                            }
                        }}
                    />
                );
            case 'clients':
                return (
                    <FirmPortalClients 
                        user={user} 
                        orgData={orgData}
                        orgConfig={orgConfig}
                    />
                );
            case 'users':
                return (
                    <FirmPortalUsers 
                        user={user} 
                        orgData={orgData}
                        orgConfig={orgConfig}
                    />
                );
             case 'analytics':
                return (
                    <FirmPortalAnalytics 
                        user={user} 
                        orgData={orgData}
                        orgConfig={orgConfig}
                    />
                );
            case 'ai-governance':
                return (
                    <FirmPortalAIGovernance 
                        user={user} 
                        orgData={orgData}
                        orgConfig={orgConfig}
                    />
                );
            case 'activity':
                return (
                    <>
                        <Typography variant="h4" gutterBottom>
                            {showEnterpriseFeatures ? 'Firm Activity Feed' : `Welcome, ${user?.name || 'Firm Administrator'}.`}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            View and monitor activity across all users and clients in your organization.
                        </Typography>
                        
                        {/* Show alerts for read-only tier (non-enterprise) */}
                        {!showEnterpriseFeatures && alerts.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <ClientAlertsSection
                                    alerts={alerts}
                                    loading={alertsLoading}
                                    onDismiss={dismissAlert}
                                    onDismissAll={dismissAllClientAlerts}
                                    onViewClient={null}
                                    onRefetch={refetchAlerts}
                                    isFirmPortal={true}
                                    compact={true}
                                />
                            </Box>
                        )}
                        
                        <FirmPortalFeed 
                            user={user} 
                            orgConfig={orgConfig}
                            configLoading={configLoading}
                            initialSearchQuery={initialSearchQuery}
                        />
                    </>
                );
            case 'account':
                return <FirmPortalAccount user={user} orgData={orgData} />;
            default:
                return null;
        }
    };

    // Show loading while checking org tier
    if (orgLoading || configLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading firm portal...</Typography>
            </Box>
        );
    }

    // Show error if org doesn't exist
if (!orgLoading && !orgData && user?.organizationId) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
            {/* Minimal Header */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: 'primary.main',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                    SavvaiEdge
                </Typography>
                <Box>
                    {userPortalAccess && (
                        <Button 
                            variant="outlined" 
                            onClick={() => navigate('/client-dashboard/insights')}
                            sx={{ mr: 2, color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                        >
                            Go to My Portal
                        </Button>
                    )}
                    <Button 
                        variant="contained" 
                        onClick={logout}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                    >
                        Logout
                    </Button>
                </Box>
            </Box>
            
            {/* Error Message */}
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <Alert severity="error" sx={{ maxWidth: 600 }}>
                    <Typography variant="h6" gutterBottom>
                        Organization Not Found
                    </Typography>
                    <Typography>
                        Your firm portal organization could not be found. It may have been deleted.
                        Please contact support if you believe this is an error.
                    </Typography>
                    {userPortalAccess && (
                        <Button 
                            variant="contained" 
                            sx={{ mt: 2 }}
                            onClick={() => navigate('/client-dashboard/insights')}
                        >
                            Return to My Portal
                        </Button>
                    )}
                </Alert>
            </Box>
        </Box>
    );
}

    // Show error if org doesn't exist
if (!orgLoading && !orgData && user?.organizationId) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
            {/* Minimal Header */}
            ...
        </Box>
    );
}

    // Check if user is deactivated
    if (user?.active === false) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
                {/* Minimal Header */}
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'primary.main',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                        SavvaiEdge
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={logout}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                    >
                        Logout
                    </Button>
                </Box>
                
                {/* Deactivated Message */}
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <Alert severity="error" sx={{ maxWidth: 600 }}>
                        <Typography variant="h6" gutterBottom>
                            Account Deactivated
                        </Typography>
                        <Typography>
                            Your Firm Portal access has been deactivated. Please contact your firm administrator 
                            if you believe this is an error.
                        </Typography>
                        <Button 
                            variant="contained" 
                            sx={{ mt: 2 }}
                            onClick={logout}
                        >
                            Logout
                        </Button>
                    </Alert>
                </Box>
            </Box>
        );
    }

    return (

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: orgConfig?.primaryColor || 'primary.main',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Co-branded header: SavvaiEdge | Partner Logo */}
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                        SavvaiEdge
                    </Typography>
                    {orgConfig?.logoUrl && (
                        <>
                            <Box sx={{ mx: 2, height: 24, borderLeft: '1px solid rgba(255,255,255,0.5)' }} />
                            <Box
                                component="img"
                                src={orgConfig.logoUrl}
                                alt={orgConfig.organizationName || 'Logo'}
                                sx={{ height: 36 }}
                            />
                        </>
                    )}
                    {/* Enterprise badge */}
                    {showEnterpriseFeatures && (
                        <Chip 
                            label="Enterprise" 
                            size="small" 
                            sx={{ 
                                ml: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)', 
                                color: 'white',
                                fontWeight: 'bold'
                            }} 
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     {/* Alert Badge - show for all firm portal users */}
                    <AlertBadge 
                        count={clientsWithAlertsCount}
                        onClick={handleAlertBadgeClick}
                    />
                    
                    {/* View Switcher - Only show for dual-access users */}
                    {isDualAccessUser && (
                        <Chip
                            icon={<SwapHorizIcon />}
                            label="Switch to My Portal"
                            onClick={handleSwitchToUserPortal}
                            sx={{ 
                                mr: 2,
                                ml: 1,
                                cursor: 'pointer',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.3)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                            }}
                        />
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={() => setIsSupportModalOpen(true)} 
                        sx={{ 
                            mr: 2, 
                            color: 'white', 
                            borderColor: 'rgba(255,255,255,0.5)',
                            '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        Contact Support
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={logout}
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        Logout
                    </Button>
                </Box>
            </Box>
            
            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2, md: 3 } }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange} 
                        aria-label="firm portal tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {tabs.map((tab, index) => (
                            <Tab key={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ flexGrow: 1, mt: 3, overflowY: 'auto' }}>
                    {renderTabContent()}
                </Box>
            </Box>
            <SupportModal open={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} user={user} />
        </Box>
    );
};

export default FirmPortal;